import { selectOption } from '@/components/ui/multi-select-utils';
import {
  AnnouncementPlacement,
  AnnouncementPlacementLabelMap,
  announcementPlacementSchema,
  AnnouncementStatus,
  AnnouncementStatusLabelMap,
  announcementStatusSchema,
  AnnouncementType,
  AnnouncementTypeLabelMap,
  announcementTypeSchema,
} from '@airreading/common/data';
import { z } from 'zod';
import { createFormOptions } from './form-utils';
import { CircleCheck, Info, ShieldAlert, TriangleAlert } from 'lucide-react';

export const MAX_ANNOUNCEMENT_CONTENT_CHARS = 1500;

export const announcementFormSchema = z.object({
  title: z.string().nullable().optional(),
  content: z
    .string()
    .min(1, 'Announcement content is required')
    .max(
      MAX_ANNOUNCEMENT_CONTENT_CHARS,
      `Announcement content must be less than ${MAX_ANNOUNCEMENT_CONTENT_CHARS} characters`,
    ),
  placement: selectOption.refine(({ value }) => !!value, {
    message: 'Placement is required',
  }),
  type: selectOption.refine(({ value }) => !!value, {
    message: 'Type is required',
  }),
  status: selectOption.refine(({ value }) => !!value, {
    message: 'Status is required',
  }),
});
export type AnnouncementFormSchema = z.infer<typeof announcementFormSchema>;

export const announcementPlacementOptions = createFormOptions<AnnouncementPlacement>(
  announcementPlacementSchema.options,
  AnnouncementPlacementLabelMap,
);

export const announcementTypeOptions = createFormOptions<AnnouncementType>(
  announcementTypeSchema.options,
  AnnouncementTypeLabelMap,
);

export const announcementStatusOptions = createFormOptions<AnnouncementStatus>(
  announcementStatusSchema.options,
  AnnouncementStatusLabelMap,
);

export interface AnnouncementTypeMetadata {
  bgClass: string;
  textClass: string;
  icon?: JSX.Element;
}

export const AnnouncementTypeMetadataMap: Record<AnnouncementType, AnnouncementTypeMetadata> = {
  general: {
    bgClass: 'bg-gray-200',
    textClass: 'text-gray-800',
  },
  informative: {
    bgClass: 'bg-blue-200',
    textClass: 'text-blue-800',
    icon: <Info className="min-w-6 min-h-6" />,
  },
  success: {
    bgClass: 'bg-green-200',
    textClass: 'text-green-800',
    icon: <CircleCheck className="min-w-6 min-h-6" />,
  },
  caution: {
    bgClass: 'bg-yellow-200',
    textClass: 'text-yellow-800',
    icon: <TriangleAlert className="min-w-6 min-h-6" />,
  },
  urgent: {
    bgClass: 'bg-red-200',
    textClass: 'text-red-800',
    icon: <ShieldAlert className="min-w-6 min-h-6" />,
  },
};
